import React, { useState, useEffect } from 'react';
import api from '../../api';
import './LeagueMetrics.css';
import PropTypes from 'prop-types';

// Import chart components
import LossTrackingChart from './charts/LossTrackingChart';
import DistributionCards from './charts/DistributionCards';
import PickDistributionChart from './charts/PickDistributionChart';
import WeeklyPerformanceChart from './charts/WeeklyPerformanceChart';
import UserPerformanceChart from './charts/UserPerformanceChart';
import LeadTimeAnalysis from './charts/LeadTimeAnalysis';
import PickTypePerformance from './charts/PickTypePerformance';
import PickLocationAnalysis from './charts/PickLocationAnalysis';

const LeagueMetrics = ({ leaderboardData }) => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const response = await api.getLeagueMetrics();
                setMetrics(response.data);
            } catch (err) {
                setError('Failed to load metrics');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchMetrics();
    }, []);

    if (loading) return <div className="metrics-loading">Loading metrics...</div>;
    if (error) return <div className="metrics-error">{error}</div>;
    if (!metrics) return null;

    const renderSummaryMetrics = () => (
        <div className="metrics-summary">
            <div className="metric-card">
                <h3>Total Picks</h3>
                <div className="metric-value">{metrics.summary.totalPicks}</div>
            </div>
            <div className="metric-card">
                <h3>Win Rate</h3>
                <div className="metric-value">
                    {metrics.summary.winRate ? metrics.summary.winRate.toFixed(1) : '0.0'}%
                </div>
            </div>
            <div className="metric-card">
                <h3>Total Wins</h3>
                <div className="metric-value">{metrics.summary.totalWins}</div>
            </div>
            <div className="metric-card">
                <h3>League Record</h3>
                <div className="metric-value">
                    {metrics.summary.totalWins}-{metrics.summary.totalLosses}
                </div>
            </div>
        </div>
    );

    const leadTimeData = leaderboardData.map(user => {
        const userMetrics = metrics?.leadTimeStats?.find?.(m => m.username === user.username) || {};

        const totalGames = (user.wins || 0) + (user.losses || 0);
        const winPercentage = totalGames > 0 ? ((user.wins || 0) / totalGames) * 100 : 0;

        return {
            username: user.username,
            avg_lead_time: userMetrics?.avg_lead_time ?? 0,
            avg_winning_lead_time: userMetrics?.avg_winning_lead_time ?? 0,
            avg_losing_lead_time: userMetrics?.avg_losing_lead_time ?? 0,
            rank: user.rank,
            win_percentage: winPercentage,
            wins: user.wins || 0,
            losses: user.losses || 0
        };
    });

    const sortedLeadTimeData = [...leadTimeData].sort((a, b) => {
        const rankA = parseInt(String(a.rank).replace('T', ''));
        const rankB = parseInt(String(b.rank).replace('T', ''));
        return rankA - rankB;
    });

    console.log('Final leadTimeData:', leadTimeData);

    return (
        <div className="metrics-dashboard">
            {renderSummaryMetrics()}

            <div className="metrics-charts">
                <LossTrackingChart data={metrics.lossTracking} />
                <DistributionCards data={metrics.distributionSplits} />
                <PickDistributionChart data={metrics.pickDistribution} />
                <WeeklyPerformanceChart data={metrics.weeklyTrends} />
                <UserPerformanceChart data={metrics.userPerformance} />
                <LeadTimeAnalysis data={sortedLeadTimeData} />
                <PickTypePerformance data={metrics.pickTypePerformance} />
                <PickLocationAnalysis data={metrics.pickLocationAnalysis} />
            </div>
        </div>
    );
};

LeagueMetrics.propTypes = {
    leaderboardData: PropTypes.arrayOf(PropTypes.shape({
        username: PropTypes.string.isRequired,
        wins: PropTypes.number,
        losses: PropTypes.number,
        rank: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ])
    })).isRequired
};

export default LeagueMetrics;
